import React, { useCallback, useEffect, useState } from 'react';
import QuoteDetailPageStyle from './style';
import { useLocation, Outlet } from 'react-router-dom';
import useQueryApi from 'src/hooks/useApiQuery';
import { getQuoteDetailVersion } from 'src/api/apiGetQuoteDetailVersion';
import { getDetailQuoteContentful } from 'src/api/apiGetDetailQuoteContentful';
import { setContentful } from 'src/redux/actions/contentfulAction';
import { setQuoteDetail } from 'src/redux/actions/quoteDetailAction';
import { useDispatch, useSelector } from 'react-redux';
import { Row, Col } from 'antd';
import HeaderQuoteDetail from './header';
import NavbarQuoteDetail from './navbar';
import { setItinerary } from 'src/redux/actions/itineraryAction';
import { quoteDetailSelector } from 'src/redux/selectors/quoteDetailSelector';
import { contentfulSelector } from 'src/redux/selectors/contentfulSelector';
import isEmpty from 'lodash/isEmpty';
import { toast } from 'react-toastify';
import FooterQuoteDetail from './footer';
import { getQuoteDetail } from 'src/api/apiGetQuoteDetail';
import Loading from 'src/components/loading';
import { setMultipleQuotesAction } from 'src/redux/actions/multipleQuotesAction';
import useIsPreview from 'src/hooks/useIsPreview';
import SectionPager from 'src/components/sectionPager';
import useQuoteTemplate from 'src/hooks/useQuoteTemplate';

const QuoteDetailPage = () => {
    const location: any = useLocation();
    const queryParams = new URLSearchParams(location.search);
    const quoteId = queryParams.get('id') || '';
    const isPreview = useIsPreview();
    const quotesArray = queryParams.get('multipleQuotes')?.split(',') || [];

    const [isLoading, setIsLoading] = useState(true);

    const dispatch = useDispatch();

    const { data: dataVersion, isLoading: isLoadingVersion } = useQueryApi(
        ['id', quoteId],
        () => getQuoteDetailVersion(quoteId),
        {
            enabled: !!quoteId && !isPreview,
        },
    );

    const fetchData = useCallback(async () => {
        setIsLoading(true);
        try {
            const [responseContentful, response]: [any, any] =
                await Promise.all([
                    getDetailQuoteContentful(),
                    quoteId ? getQuoteDetail(quoteId) : Promise.resolve(null),
                ]);
            setDataQuote(
                responseContentful?.fields,
                response?.introduction,
                response?.quoteItinerary,
            );
        } catch (err: any) {
            toast.error(err?.message || '');
        } finally {
            setIsLoading(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [quoteId]);

    useEffect(() => {
        if (isPreview) {
            dispatch(
                setMultipleQuotesAction({
                    ids: quotesArray.length > 1 ? quotesArray : [],
                    currentId: queryParams.get('id'),
                }),
            );
            fetchData();
        } else {
            const data = (dataVersion as { data: any })?.data;
            setDataQuote(
                data?.contentful,
                data?.introduction,
                data?.quoteItinerary,
            );
            if (!isLoadingVersion) {
                setIsLoading(false);
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [dataVersion, fetchData, isPreview, isLoadingVersion,]);

    const combinedLoading = !isLoadingVersion && !isLoading;

    const setDataQuote = (
        contentfulData: any,
        quoteDetailData: any,
        itinerary: any,
    ) => {
        if (contentfulData) {
            dispatch(setContentful(contentfulData));
        }
        if (quoteDetailData) {
            dispatch(setQuoteDetail(quoteDetailData));
        }
        if (itinerary) {
            dispatch(setItinerary(itinerary));
        }
    };

    const quoteDetail = useSelector(quoteDetailSelector);
    const contentfulData = useSelector(contentfulSelector);
    const { isSafariLayout } = useQuoteTemplate();

    return (
        <>
            {!combinedLoading && <Loading />}
            {!isEmpty(quoteDetail) && !isEmpty(contentfulData) && (
                <QuoteDetailPageStyle>
                    <HeaderQuoteDetail isPreview={isPreview} />
                    <div className="container-ydp detail-content">
                        <Row gutter={[24, 16]}>
                            <Col span={24} lg={{ order: 2, flex: '367px' }}>
                                <NavbarQuoteDetail isPreview={isPreview} />
                            </Col>
                            <Col
                                span={24}
                                lg={{ order: 1, flex: '1 1 0' }}
                                style={{ minWidth: 0 }}
                                id="quote-detail"
                            >
                                <Outlet />
                                {!isSafariLayout && <SectionPager />}
                            </Col>
                        </Row>
                    </div>
                    <FooterQuoteDetail isPreview={isPreview} />
                </QuoteDetailPageStyle>
            )}
        </>
    );
};

export default QuoteDetailPage;
